import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';

declare const Stripe: any;

let nextId = 0;

@Component({
  selector: 'app-stripe-card-element',
  standalone: true,
  templateUrl: './stripe-card-element.component.html',
  styleUrl: './stripe-card-element.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StripeCardElementComponent implements OnInit, OnDestroy {
  publishableKey = input.required<string>();

  readonly isLoading = signal(true);
  readonly stripeError = signal<string | null>(null);
  readonly cardError = signal<string | null>(null);

  readonly mountId = `stripe-card-el-${nextId++}`;

  @ViewChild('cardMount', { static: true }) cardMountRef!: ElementRef<HTMLDivElement>;

  private stripeInstance: any = null;
  private cardElement: any = null;

  ngOnInit(): void {
    this.initStripe();
  }

  ngOnDestroy(): void {
    this.cardElement?.destroy();
    this.cardElement = null;
    this.stripeInstance = null;
  }

  /**
   * Call from the parent to tokenise the card.
   * Resolves with a Stripe PaymentMethod id (`pm_xxx`).
   */
  async createPaymentMethod(): Promise<string> {
    if (!this.stripeInstance || !this.cardElement) {
      throw new Error('Stripe is not initialised');
    }

    const { error, paymentMethod } = await this.stripeInstance.createPaymentMethod({
      type: 'card',
      card: this.cardElement
    });

    if (error) {
      this.cardError.set(error.message ?? 'Card verification failed.');
      throw new Error(error.message ?? 'Card verification failed.');
    }

    return paymentMethod.id;
  }

  private async initStripe(): Promise<void> {
    const key = this.publishableKey();
    if (!key) {
      this.stripeError.set('Stripe publishable key is not available.');
      this.isLoading.set(false);
      return;
    }

    try {
      await this.loadStripeScript();

      this.stripeInstance = Stripe(key);
      const elements = this.stripeInstance.elements();
      this.cardElement = elements.create('card', {
        style: {
          base: {
            fontSize: '15px',
            color: '#333',
            fontFamily: 'var(--font-family-primary, Satoshi), sans-serif',
            '::placeholder': { color: '#aab7c4' }
          },
          invalid: { color: '#dc2626' }
        }
      });

      await new Promise(resolve => setTimeout(resolve, 0));
      this.cardElement.mount(this.cardMountRef.nativeElement);

      this.cardElement.on('change', (event: any) => {
        this.cardError.set(event.error?.message ?? null);
      });

      this.isLoading.set(false);
    } catch (err) {
      console.error('[StripeCardElement] init failed:', err);
      this.stripeError.set('Failed to load payment form. Please try again later.');
      this.isLoading.set(false);
    }
  }

  private loadStripeScript(): Promise<void> {
    if (typeof Stripe !== 'undefined') return Promise.resolve();

    const existing = document.querySelector('script[src="https://js.stripe.com/v3/"]');
    if (existing) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Stripe.js'));
      document.head.appendChild(script);
    });
  }
}
